/**
 * My Company Tab - Displays company information
 */
import { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react'
import { useAuth, CompanyCard, Card, type AgentPlatEntity, type CompanyLocalData } from '@trafiflow/shared-ui'
import { useMetaConnection } from '../../../../shared/contexts'
import apiClient from '../../../../shared/lib/api'
import WebsiteUrlModal from '../../components/WebsiteUrlModal'
import { Rocket, Clock, Link2, Globe, RefreshCw, Loader2 } from 'lucide-react'

// Company entity types are imported from @trafiflow/shared-ui

interface CompanyProfile {
  overview?: {
    businessName?: string
    website?: string
    primaryMarket?: string
    brandSummary?: string
    targetRegions?: string[]
    contactEmail?: string
    contactPhone?: string
    productOffer?: string
    topProducts?: Record<string, unknown>[]
    dataSources?: string[]
    lastSyncedAt?: string | null
    associatedUrls?: string[]
    socialMediaPages?: Record<string, unknown>[]
    advertisingPages?: Record<string, unknown>[]
    competitors?: Record<string, unknown>[]
  }
  analyses?: Record<string, unknown>
}

interface MyCompanyResponse {
  profile: CompanyProfile
  agentplatEntity?: AgentPlatEntity
}

export interface MyCompanyTabRef {
  refresh: () => void
}

const MyCompanyTab = forwardRef<MyCompanyTabRef>((_props, ref) => {
  const { user } = useAuth()
  const { connection: metaConnection } = useMetaConnection()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<MyCompanyResponse | null>(null)
  const [showWebsiteModal, setShowWebsiteModal] = useState(false)
  const [metaPageId, setMetaPageId] = useState<string | null>(null)
  const [metaPageIdLoading, setMetaPageIdLoading] = useState(false)
  const [metaPageIdFetched, setMetaPageIdFetched] = useState(false)

  const fetchCompanyData = useCallback(async () => {
    if (!user?.tenantId) return

    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get('/my-company')
      setData(response.data)
      
      // Log successful response details
      if (response.data) {
        console.log('[MyCompanyTab] API Response received:', {
          hasProfile: !!response.data.profile,
          hasAgentPlatEntity: !!response.data.agentplatEntity,
          profileKeys: response.data.profile ? Object.keys(response.data.profile) : [],
        })
      }
    } catch (err: unknown) {
      const error = err as { message?: string; response?: { status?: number; data?: { message?: string } }; config?: { url?: string } }
      console.group('❌ My Company - Error fetching data')
      console.error('Error:', error)
      console.log('Error message:', error.message)
      console.log('Response status:', error.response?.status)
      console.log('Response data:', error.response?.data)
      console.log('Request URL:', error.config?.url)
      console.log('User Tenant ID:', user?.tenantId)
      console.groupEnd()
      setError(error.response?.data?.message || 'Failed to load company information')
    } finally {
      setLoading(false)
    }
  }, [user?.tenantId])

  useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchCompanyData()
    },
  }))

  useEffect(() => {
    fetchCompanyData()
  }, [fetchCompanyData])

  // Mostrar modal si no hay website después de cargar los datos
  useEffect(() => {
    if (!loading && data && !data.profile?.overview?.website) {
      // Solo mostrar el modal si no hay website y no hay entity ID (indicando que no se ha configurado)
      if (!data.agentplatEntity?.id || data.agentplatEntity.id === '') {
        setShowWebsiteModal(true)
      }
    }
  }, [loading, data])

  // Log diagnostic information when agentplatEntity is not available
  useEffect(() => {
    if (!loading && data && !data.agentplatEntity) {
      console.group('🔍 My Company - Diagnostic Information')
      console.log('Status: Company entity information not available')
      console.log('Timestamp:', new Date().toISOString())
      console.log('User Tenant ID:', user?.tenantId || 'Not available')
      
      // Detailed profile information
      if (data.profile) {
        console.group('📋 Profile Data')
        console.log('Has profile:', !!data.profile)
        console.log('Has overview:', !!data.profile.overview)
        if (data.profile.overview) {
          const overviewKeys = Object.keys(data.profile.overview)
          console.log('Overview keys:', overviewKeys)
          const hasData = overviewKeys.some(key => {
            const value = data.profile.overview?.[key as keyof typeof data.profile.overview]
            if (Array.isArray(value)) return value.length > 0
            return value !== null && value !== undefined && value !== ''
          })
          console.log('Has profile data:', hasData)
          if (hasData) {
            console.log('Profile overview:', JSON.stringify(data.profile.overview, null, 2))
          } else {
            console.log('Profile overview is empty')
          }
        }
        console.log('Has analyses:', !!data.profile.analyses)
        if (data.profile.analyses) {
          console.log('Analyses:', JSON.stringify(data.profile.analyses, null, 2))
        }
        console.groupEnd()
      } else {
        console.log('Profile Data: Not available')
      }
      
      // Full response data
      console.group('📦 Full Response Data')
      console.log(JSON.stringify(data, null, 2))
      console.groupEnd()
      
      // Analysis
      console.group('🔎 Analysis')
      console.log('agentplatEntity present:', !!data.agentplatEntity)
      console.log('profile present:', !!data.profile)
      console.log('profile.overview present:', !!data.profile?.overview)
      if (data.profile?.overview) {
        const overview = data.profile.overview
        console.log('Profile overview fields with data:')
        Object.keys(overview).forEach(key => {
          const value = overview[key as keyof typeof overview]
          if (Array.isArray(value)) {
            if (value.length > 0) console.log(`  ✓ ${key}: [${value.length} items]`)
          } else if (value !== null && value !== undefined && value !== '') {
            console.log(`  ✓ ${key}:`, value)
          }
        })
      }
      console.groupEnd()
      
      // Possible reasons
      console.group('💡 Possible reasons')
      console.log('  - Company entity not yet created')
      console.log('  - Integration not configured')
      console.log('  - Data synchronization pending')
      console.log('  - API endpoint returned empty agentplatEntity')
      console.groupEnd()
      
      console.groupEnd()
    }
  }, [loading, data, user?.tenantId])

  // Website: profile first, then AgentPlat entity (domain/metadata) cuando el perfil no tiene URL
  const websiteUrl = (() => {
    const fromProfile = data?.profile?.overview?.website
    if (fromProfile) return fromProfile
    const entity = data?.agentplatEntity
    if (!entity) return undefined
    const fromMeta = entity.metadata?.profile?.overview?.website ?? entity.metadata?.website
    if (fromMeta) return fromMeta
    if (entity.domain) {
      const d = String(entity.domain).trim()
      return d.startsWith('http') ? d : `https://${d}`
    }
    return undefined
  })()

  const fetchMetaPageId = useCallback(async () => {
    const website = websiteUrl
    if (!website) return

    setMetaPageIdLoading(true)
    setMetaPageId(null)
    setMetaPageIdFetched(false)
    try {
      const url = website.startsWith('http') ? website : `https://${website}`
      const response = await apiClient.get('/my-company/meta-page-id', {
        params: { url },
        timeout: 95000, // ~70s backend + buffer
      })
      const pageId = response.data?.pageId ?? ''
      setMetaPageId(pageId || null)
      setMetaPageIdFetched(true)
    } catch (err) {
      console.error('[MyCompanyTab] Error fetching Meta Page ID:', err)
      setMetaPageId(null)
      setMetaPageIdFetched(true)
    } finally {
      setMetaPageIdLoading(false)
    }
  }, [websiteUrl])

  // Reset Meta Page ID state when website URL changes
  useEffect(() => {
    setMetaPageId(null)
    setMetaPageIdFetched(false)
  }, [websiteUrl])

  // Fetch Meta Page ID when we have a website and haven't fetched yet
  useEffect(() => {
    if (websiteUrl && !metaPageIdLoading && !metaPageIdFetched) {
      fetchMetaPageId()
    }
  }, [websiteUrl, metaPageIdLoading, metaPageIdFetched, fetchMetaPageId])

  const handleSaveWebsiteUrl = async (url: string) => {
    if (!user?.tenantId) {
      throw new Error('User not authenticated')
    }

    // Actualizar el perfil con la nueva URL
    const currentProfile = data?.profile || {
      overview: {},
      analyses: {},
    }

    const updatedProfile = {
      ...currentProfile,
      overview: {
        ...currentProfile.overview,
        website: url,
      },
    }

    // Llamar al API para actualizar
    await apiClient.put('/my-company', {
      tenantId: user.tenantId,
      profile: updatedProfile,
    })

    // Recargar los datos para obtener la entidad actualizada
    await fetchCompanyData()
  }

  const { profile, agentplatEntity } = data || {}

  // Prepare local data for CompanyCard
  const localData: CompanyLocalData | undefined = profile?.overview
    ? {
        businessName: profile.overview.businessName,
        website: profile.overview.website,
        primaryMarket: profile.overview.primaryMarket,
        brandSummary: profile.overview.brandSummary,
        contactEmail: profile.overview.contactEmail,
        contactPhone: profile.overview.contactPhone,
        productOffer: profile.overview.productOffer,
        targetRegions: profile.overview.targetRegions,
      }
    : undefined

  // Extract social media information from entity metadata or profile
  // Try entity metadata first (format: { platform: url })
  let socialMedia = agentplatEntity?.metadata?.social_media
  
  // If not found, try profile.overview.socialMediaPages (format: [{ platform, url, ... }])
  if (!socialMedia && profile?.overview?.socialMediaPages && Array.isArray(profile.overview.socialMediaPages) && profile.overview.socialMediaPages.length > 0) {
    // Transform array format to object format
    socialMedia = profile.overview.socialMediaPages.reduce((acc: Record<string, string>, page: Record<string, unknown>) => {
      if (typeof page.platform === 'string' && typeof page.url === 'string') {
        acc[page.platform] = page.url
      }
      return acc
    }, {})
  }
  
  const hasSocialMedia = socialMedia && typeof socialMedia === 'object' && Object.keys(socialMedia).length > 0
  
  // Debug logging
  useEffect(() => {
    if (!loading && data) {
      console.log('[MyCompanyTab] Social Media Debug:', {
        hasEntityMetadata: !!agentplatEntity?.metadata?.social_media,
        entitySocialMedia: agentplatEntity?.metadata?.social_media,
        hasProfileSocialMedia: !!profile?.overview?.socialMediaPages,
        profileSocialMedia: profile?.overview?.socialMediaPages,
        transformedSocialMedia: socialMedia,
        hasSocialMedia,
      })
    }
  }, [loading, data, socialMedia, hasSocialMedia, agentplatEntity?.metadata?.social_media, profile?.overview?.socialMediaPages])

  return (
    <div className="space-y-6">
      {/* Modal para pedir URL principal */}
      <WebsiteUrlModal
        isOpen={showWebsiteModal}
        onClose={() => setShowWebsiteModal(false)}
        onSave={handleSaveWebsiteUrl}
        currentUrl={profile?.overview?.website || ''}
      />
      
      {/* Company Information Card - Displays all company information and local data */}
      <CompanyCard
        entity={agentplatEntity || null}
        localData={localData}
        loading={loading}
        error={error}
        showEditWebsite={true}
        onEditWebsite={() => setShowWebsiteModal(true)}
        showMetadata={true}
        excludeMetadataFields={['social_media']}
      />

      {/* Social Section */}
      <Card className="overflow-hidden border border-tf-border">
        <div className="px-6 py-5 bg-tf-bg-muted border-b border-tf-border-sub">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-tf-bg-hover flex items-center justify-center">
              <Link2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-tf-text">Social Profiles</h2>
              <p className="text-sm text-tf-text-muted">Linked social media accounts</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          {hasSocialMedia ? (
            <div className="flex flex-wrap gap-3">
              {Object.entries(socialMedia as Record<string, unknown>).map(
                ([platform, url]) => (
                  <a
                    key={platform}
                    href={url as string}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2.5 px-4 py-3 rounded-lg bg-tf-bg-muted border border-tf-border text-sm font-medium text-tf-text-sub hover:bg-tf-bg-muted hover:border-primary/30 hover:text-primary transition-all"
                  >
                    <span className="capitalize">{platform}</span>
                    <Globe className="w-3.5 h-3.5 text-tf-text-muted group-hover:text-primary transition-colors" />
                  </a>
                )
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 py-2 text-tf-text-muted">
              <Clock className="w-5 h-5 text-tf-text-muted" />
              <span className="text-sm">Social profiles will appear here once synced</span>
            </div>
          )}
        </div>
      </Card>

      {/* Ads Section */}
      <Card className="overflow-hidden border border-tf-border">
        <div className="px-6 py-5 bg-tf-bg-muted border-b border-tf-border-sub">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-tf-feedback-warning-bg flex items-center justify-center">
              <Rocket className="w-5 h-5 text-tf-feedback-warning-text" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-tf-text">Advertising</h2>
              <p className="text-sm text-tf-text-muted">Campaigns and ad analytics</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* Meta Ads Account ID */}
          {metaConnection.connected && metaConnection.accountId ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-tf-text-sub">Meta Ads Account ID</p>
              <code className="block px-4 py-3 rounded-lg bg-tf-bg-hover border border-tf-border text-sm font-mono text-tf-text">
                {metaConnection.accountId}
              </code>
              {metaConnection.accountName && (
                <p className="text-sm text-tf-text-muted">{metaConnection.accountName}</p>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 py-2 text-tf-text-muted">
              <Clock className="w-5 h-5 text-tf-text-muted shrink-0" />
              <span className="text-sm">Connect your Meta Ads account in Settings to view your account ID</span>
            </div>
          )}

          {/* Meta Ads Page ID */}
          <div className="pt-4 border-t border-tf-border-sub">
            <p className="text-sm font-medium text-tf-text-sub mb-2">Meta Ads Page ID</p>
            {!websiteUrl ? (
              <div className="flex items-center gap-3 py-2 text-tf-text-muted">
                <Clock className="w-5 h-5 text-tf-text-muted shrink-0" />
                <span className="text-sm">Add a website to your company profile to fetch the Page ID</span>
              </div>
            ) : metaPageIdLoading ? (
              <div className="flex items-center gap-3 py-2 text-tf-text-sub">
                <Loader2 className="w-5 h-5 text-tf-feedback-warning-text shrink-0 animate-spin" />
                <span className="text-sm">Fetching Page ID from AgentPlat (may take up to 70 seconds)…</span>
              </div>
            ) : metaPageIdFetched && metaPageId ? (
              <code className="block px-4 py-3 rounded-lg bg-tf-bg-hover border border-tf-border text-sm font-mono text-tf-text">
                {metaPageId}
              </code>
            ) : metaPageIdFetched && !metaPageId ? (
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-tf-feedback-warning-text font-medium">Not found</span>
                <button
                  type="button"
                  onClick={fetchMetaPageId}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-tf-feedback-warning-bg border border-tf-feedback-warning-border text-sm font-medium text-tf-feedback-warning-text hover:bg-tf-feedback-warning-bg transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reintentar
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </Card>
    </div>
  )
})

MyCompanyTab.displayName = 'MyCompanyTab'

export default MyCompanyTab
